import React, { useState, useEffect, useRef } from "react";
import Button from "../../Buttons/Button";
import { createPortal } from "react-dom";

const sizeClasses = {
  xs: "px-2 py-0",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

// Password pattern regex - exactly as you specified
const PASSWORD_PATTERN =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[&()#*$@])[A-Za-z\d&()#*$@]+$/;

// Character mapping focused on your specific special characters
const defaultCharacterMapping = {
  a: "@",
  b: "&",
  c: "(",
  d: ")",
  e: "#",
  f: "*",
  g: "$",
  h: "@",
  i: "&",
  j: "(",
  k: ")",
  l: "#",
  m: "*",
  n: "$",
  o: "0",
  p: "9",
  q: "8",
  r: "7",
  s: "$",
  t: "6",
  u: "5",
  v: "4",
  w: "3",
  x: "2",
  y: "1",
  z: "@",
};

const CustomPassword = ({
  value,
  onChange,
  noGenerate,
  placeholder = "Enter text...",
  size = "md",
  rounded = "rounded",
  disabled = false,
  passwordFormat = {},
  customCharacterMapping = {},
  showValidation = true,
  shadow = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const buttonRef = useRef(null);
  const dropdownContainerRef = useRef(null);

  // Merge custom mapping with default mapping
  const finalCharacterMapping = {
    ...defaultCharacterMapping,
    ...customCharacterMapping,
  };

  // Merge provided format with defaults
  const defaultFormat = {
    base: "",
    minLength: 8,
    maxLength: 20,
    hasUpperCase: true,
    hasLowerCase: true,
    hasSpecialChar: true,
    hasNumber: true,
    allowedSpecialChars: "&()#*$@", // Only these special characters
  };

  const mergedFormat = { ...defaultFormat, ...passwordFormat };

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Update dropdown position when it opens
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
      const handleResize = () => calculateDropdownPosition();
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleResize);
      };
    }
  }, [isOpen]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event) => {
        const isClickOnInput = buttonRef.current?.contains(event.target);
        const isClickOnDropdown = dropdownContainerRef.current?.contains(
          event.target
        );

        if (!isClickOnInput && !isClickOnDropdown) {
          setIsOpen(false);
          setIsFocused(false);
        }
      };

      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Validate password against the pattern
  const validatePassword = (password) => {
    return PASSWORD_PATTERN.test(password);
  };

  // Get validation status details
  const getValidationDetails = (password) => {
    return {
      hasDigit: /\d/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasSpecialChar: /[&()#*$@]/.test(password),
      hasOnlyAllowedChars: /^[A-Za-z\d&()#*$@]+$/.test(password),
      isValidLength:
        password.length >= mergedFormat.minLength &&
        password.length <= mergedFormat.maxLength,
    };
  };

  // Transform base string using character mapping and case variations
  const transformBaseString = (base, format, charMapping) => {
    if (!base) return "";

    console.log("Transforming base:", base);
    let transformed = "";

    // Apply character mapping and case variations to each character
    for (let i = 0; i < base.length; i++) {
      const char = base[i];
      const lowerChar = char.toLowerCase();

      // Decide transformation type for this character
      const transformationType = Math.random();

      if (transformationType < 0.3 && charMapping[lowerChar]) {
        // 30% chance: Use character mapping (could be special char or number)
        const mappedChar = charMapping[lowerChar];
        transformed += mappedChar;
      } else if (transformationType < 0.6) {
        // 30% chance: Make uppercase
        transformed += char.toUpperCase();
      } else if (transformationType < 0.9) {
        // 30% chance: Make lowercase
        transformed += char.toLowerCase();
      } else {
        // 10% chance: Keep original
        transformed += char;
      }
    }

    console.log("After transformation:", transformed);
    return transformed;
  };

  // Generate random characters to fill remaining length
  const generateRandomChars = (length, format) => {
    const upperCase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowerCase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const specialChars = format.allowedSpecialChars || "&()#*$@";

    let charset = "";
    if (format.hasUpperCase) charset += upperCase;
    if (format.hasLowerCase) charset += lowerCase;
    if (format.hasNumber) charset += numbers;
    if (format.hasSpecialChar) charset += specialChars;

    let randomChars = "";
    for (let i = 0; i < length; i++) {
      if (charset) {
        randomChars += charset[Math.floor(Math.random() * charset.length)];
      }
    }
    return randomChars;
  };

  // Ensure password meets all requirements
  const ensurePasswordRequirements = (password, format) => {
    const upperCase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowerCase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const specialChars = format.allowedSpecialChars || "&()#*$@";

    const validation = getValidationDetails(password);
    const passwordArray = password.split("");

    // Find positions we can modify (prefer end positions to preserve base)
    const modifiablePositions = [];
    for (let i = passwordArray.length - 1; i >= 0; i--) {
      modifiablePositions.push(i);
    }

    let modificationIndex = 0;

    // Ensure we have required character types
    if (
      format.hasUpperCase &&
      !validation.hasUpperCase &&
      modificationIndex < modifiablePositions.length
    ) {
      const pos = modifiablePositions[modificationIndex++];
      passwordArray[pos] =
        upperCase[Math.floor(Math.random() * upperCase.length)];
    }

    if (
      format.hasLowerCase &&
      !validation.hasLowerCase &&
      modificationIndex < modifiablePositions.length
    ) {
      const pos = modifiablePositions[modificationIndex++];
      passwordArray[pos] =
        lowerCase[Math.floor(Math.random() * lowerCase.length)];
    }

    if (
      format.hasNumber &&
      !validation.hasDigit &&
      modificationIndex < modifiablePositions.length
    ) {
      const pos = modifiablePositions[modificationIndex++];
      passwordArray[pos] = numbers[Math.floor(Math.random() * numbers.length)];
    }

    if (
      format.hasSpecialChar &&
      !validation.hasSpecialChar &&
      modificationIndex < modifiablePositions.length
    ) {
      const pos = modifiablePositions[modificationIndex++];
      passwordArray[pos] =
        specialChars[Math.floor(Math.random() * specialChars.length)];
    }

    return passwordArray.join("");
  };

  // Enhanced password generation with base consideration
  const generateRandomString = (format, charMapping) => {
    const upperCase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowerCase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const specialChars = format.allowedSpecialChars || "&()#*$@";

    let password = "";

    // Step 1: Determine target length between minLength and maxLength
    const targetLength =
      Math.floor(Math.random() * (format.maxLength - format.minLength + 1)) +
      format.minLength;

    // Step 2: Handle base string if provided
    if (format.base && format.base.trim()) {
      console.log("Using base:", format.base.trim());

      // Transform the base string using character mapping
      let transformedBase = transformBaseString(
        format.base.trim(),
        format,
        charMapping
      );

      console.log("Transformed base:", transformedBase);

      // Truncate base if it's longer than targetLength
      if (transformedBase.length > targetLength) {
        transformedBase = transformedBase.substring(0, targetLength);
        console.log("Truncated base to targetLength:", transformedBase);
      }

      password = transformedBase;

      // Step 3: Add random characters if we need more length
      if (password.length < targetLength) {
        const remainingLength = targetLength - password.length;
        const randomChars = generateRandomChars(remainingLength, format);
        password += randomChars;
        console.log("Added random chars for targetLength:", password);
      }

      // Step 4: Ensure all requirements are met
      password = ensurePasswordRequirements(password, format);
      console.log("Final password with requirements:", password);

      return password;
    }

    // Fallback: Generate completely random password if no base
    console.log("No base provided, generating random password");

    // Ensure we have at least one of each required type
    let requiredChars = "";
    if (format.hasUpperCase) {
      requiredChars += upperCase[Math.floor(Math.random() * upperCase.length)];
    }
    if (format.hasLowerCase) {
      requiredChars += lowerCase[Math.floor(Math.random() * lowerCase.length)];
    }
    if (format.hasNumber) {
      requiredChars += numbers[Math.floor(Math.random() * numbers.length)];
    }
    if (format.hasSpecialChar) {
      requiredChars +=
        specialChars[Math.floor(Math.random() * specialChars.length)];
    }

    password = requiredChars;

    // Build charset for remaining characters
    let charset = "";
    if (format.hasUpperCase) charset += upperCase;
    if (format.hasLowerCase) charset += lowerCase;
    if (format.hasNumber) charset += numbers;
    if (format.hasSpecialChar) charset += specialChars;

    // Fill remaining space
    const stillNeed = Math.max(0, targetLength - password.length);
    for (let i = 0; i < stillNeed; i++) {
      if (charset) {
        password += charset[Math.floor(Math.random() * charset.length)];
      }
    }

    // Shuffle the password to randomize position of required characters
    const passwordArray = password.split("");
    for (let i = passwordArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [passwordArray[i], passwordArray[j]] = [
        passwordArray[j],
        passwordArray[i],
      ];
    }

    return passwordArray.join("");
  };

  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [password, setPassword] = useState(value || "");

  // Get current validation status
  const validationDetails = getValidationDetails(password);
  const isValid = validatePassword(password) && validationDetails.isValidLength;

  // Update local state when value prop changes
  useEffect(() => {
    setPassword(value || "");
  }, [value]);

  const handleGenerateNewPassword = (e) => {
    e.preventDefault();
    const newPassword = generateRandomString(
      mergedFormat,
      finalCharacterMapping
    );
    setPassword(newPassword);
    if (onChange) {
      onChange({ target: { value: newPassword } });
    }
  };

  const handleTogglePassword = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
      console.log("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const filteredValue = newValue.replace(/[^A-Za-z\d&()#*$@]/g, "");
    setPassword(filteredValue);
    if (onChange) onChange({ target: { value: filteredValue } });
  };

  const handleKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ];
    const isAllowedChar = /[A-Za-z\d&()#*$@]/.test(e.key);

    if (!isAllowedChar && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (showValidation) {
      setIsOpen(true);
    }
  };

  const handleBlur = (e) => {
    const isClickingButton =
      e.relatedTarget && e.relatedTarget.closest(".password-buttons");

    if (!isClickingButton) {
      setIsFocused(false);
      setTimeout(() => {
        setIsOpen(false);
      }, 150);
    }
  };

  return (
    <div className="flex flex-col">
      <div
        ref={buttonRef}
        className={`overflow-hidden flex-1 relative flex items-center rounded border  focus:outline-none 
          ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
          ${isFocused ? "border-primary outline-none" : ""}
          ${
            disabled
              ? "border-neutrals-200 bg-neutrals-200 cursor-not-allowed text-black"
              : "hover:border-primary focus:border-primary"
          } `}
      >
        <input
          type={showPassword ? "text" : "password"}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={password}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`for-multiple w-full flex-1 border-0 focus:outline-none ${
            sizeClasses[size]
          } ${fontSizeClasses[size]} ${rounded} rounded-l-none
          ${
            disabled
              ? "bg-neutrals-200 cursor-not-allowed text-black"
              : "bg-white"
          }`}
        />
        <div className="flex gap-0.5 items-end password-buttons">
          <Button
            type="icon"
            icon={showPassword ? "visibility_off" : "visibility"}
            onClick={handleTogglePassword}
            variant="text"
            color={showPassword ? "black" : "info"}
            size="sm"
            disabled={disabled}
          />
          {!noGenerate && (
            <Button
              type="icon"
              icon="contextual_token_add"
              variant="text"
              onClick={handleGenerateNewPassword}
              color="black"
              size="sm"
              disabled={disabled}
            />
          )}
        </div>
      </div>

      {isOpen &&
        showValidation &&
        !noGenerate &&
        createPortal(
          <div
            ref={dropdownContainerRef}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
            className="z-53 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
          >
            <div
              className={`font-medium mb-2 ${
                isValid ? "text-green-600" : "text-red-600"
              }`}
            >
              {isValid
                ? "✓ Password meets all requirements"
                : "✗ Password requirements not met"}
            </div>
            <div className="space-y-1 text-sm">
              <div
                className={
                  validationDetails.hasDigit ? "text-green-600" : "text-red-600"
                }
              >
                {validationDetails.hasDigit ? "✓" : "✗"} At least one digit
              </div>
              <div
                className={
                  validationDetails.hasLowerCase
                    ? "text-green-600"
                    : "text-red-600"
                }
              >
                {validationDetails.hasLowerCase ? "✓" : "✗"} At least one
                lowercase letter
              </div>
              <div
                className={
                  validationDetails.hasUpperCase
                    ? "text-green-600"
                    : "text-red-600"
                }
              >
                {validationDetails.hasUpperCase ? "✓" : "✗"} At least one
                uppercase letter
              </div>
              <div
                className={
                  validationDetails.hasSpecialChar
                    ? "text-green-600"
                    : "text-red-600"
                }
              >
                {validationDetails.hasSpecialChar ? "✓" : "✗"} At least one
                special character (&()#*$@)
              </div>
              <div
                className={
                  validationDetails.hasOnlyAllowedChars
                    ? "text-green-600"
                    : "text-red-600"
                }
              >
                {validationDetails.hasOnlyAllowedChars ? "✓" : "✗"} Only allowed
                characters (A-Z, a-z, 0-9, &()#*$@)
              </div>
              <div
                className={
                  validationDetails.isValidLength
                    ? "text-green-600"
                    : "text-red-600"
                }
              >
                {validationDetails.isValidLength ? "✓" : "✗"} Length between{" "}
                {mergedFormat.minLength}-{mergedFormat.maxLength} characters
              </div>
            </div>
          </div>,
          document.querySelector(".select-root")
        )}
    </div>
  );
};

export default CustomPassword;
